import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Heart, Scale, MessageSquare, ListChecks, Trash2, MessageCircle } from "lucide-react";
import { api, priceLabel, inr, fmtDate } from "@/lib/loop";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { VerificationBadge } from "@/components/Badges";
import { ChatInbox } from "@/components/Chat";
import { toast } from "sonner";

const CHECKS = [
  "Verify seller identity", "Verify ownership documents", "Check encumbrances",
  "Verify land classification/conversion", "Verify approvals", "Verify RERA where applicable",
  "Confirm dimensions", "Confirm road access", "Check outstanding dues",
  "Conduct physical inspection", "Obtain independent legal review", "Confirm transaction terms",
];

export default function BuyerDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [saved, setSaved] = useState([]);
  const [enq, setEnq] = useState([]);
  const [checkProp, setCheckProp] = useState("");
  const [checks, setChecks] = useState({});

  const load = () => {
    api.get("/my/saved").then((r) => { setSaved(r.data); if (r.data[0] && !checkProp) setCheckProp(r.data[0].id); });
    api.get("/my/enquiries").then((r) => setEnq(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (checkProp) api.get(`/my/checklist/${checkProp}`).then((r) => setChecks(r.data || {}));
  }, [checkProp]);

  const remove = async (id) => { await api.delete(`/saved/${id}`); toast("Removed"); load(); };
  const toggleCheck = async (item) => {
    const next = { ...checks, [item]: !checks[item] };
    setChecks(next);
    await api.put("/my/checklist", { property_id: checkProp, items: next });
  };

  const compareRows = [
    ["Price", (p) => priceLabel(p)],
    ["Area", (p) => p.area_display || `${p.area_sqft} sqft`],
    ["₹/sqft", (p) => (p.price_per_sqft ? inr(p.price_per_sqft) : "—")],
    ["Location", (p) => `${p.locality}, ${p.city}`],
    ["Road", (p) => p.road_width || "—"],
    ["Verification", (p) => p.verification_status.replace("_", " ")],
    ["Documents", (p) => `${(p.documents || []).filter((d) => d.status !== "not_provided").length}/${(p.documents || []).length}`],
    ["Last updated", (p) => fmtDate(p.last_verified)],
  ];

  const done = CHECKS.filter((c) => checks[c]).length;
  const defaultTab = sp.get("tab") === "enquiries" ? "enquiries" : "shortlist";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8 pb-28 md:pb-16">
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#C5A059] font-semibold">Buyer Dashboard</div>
      <h1 className="mt-1 text-2xl font-bold text-white">Welcome, {user?.name?.split(" ")[0]}</h1>

      <Tabs defaultValue={defaultTab} className="mt-6">
        <TabsList className="bg-[#0B132B] border border-white/10 flex-wrap h-auto">
          {[["shortlist", "My Shortlist", Heart], ["compare", "Compare", Scale], ["messages", "Messages", MessageCircle], ["enquiries", "Enquiries", MessageSquare], ["checklist", "Due Diligence", ListChecks]].map(([v, l, Icon]) => (
            <TabsTrigger key={v} value={v} data-testid={`buyer-tab-${v}`} className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-[#070C1E] text-slate-300">
              <Icon className="w-4 h-4 mr-1.5" /> {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="shortlist" className="mt-6">
          {saved.length === 0 ? <Empty nav={nav} /> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {saved.map((p) => (
                <div key={p.id} data-testid={`saved-${p.loop_id}`} className="bg-[#0B132B] border border-white/10 rounded-2xl overflow-hidden">
                  <img src={p.images?.[0]} alt="" className="w-full h-40 object-cover cursor-pointer" onClick={() => nav(`/property/${p.id}`)} />
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-sm font-bold text-white line-clamp-1">{p.title}</h3>
                      <button data-testid={`remove-saved-${p.loop_id}`} onClick={() => remove(p.id)} className="text-slate-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="mt-1 text-sm text-[#C5A059] font-semibold">{priceLabel(p)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="compare" className="mt-6">
          {saved.length === 0 ? <Empty nav={nav} /> : (
            <div className="overflow-x-auto bg-[#0B132B] border border-white/10 rounded-2xl">
              <table className="w-full text-sm" data-testid="compare-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-slate-500 font-medium sticky left-0 bg-[#0B132B]">Attribute</th>
                    {saved.slice(0, 4).map((p) => (
                      <th key={p.id} className="text-left p-4 min-w-[180px]">
                        <div className="text-white font-semibold line-clamp-1">{p.title}</div>
                        <VerificationBadge status={p.verification_status} className="mt-1" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {compareRows.map(([label, fn]) => (
                    <tr key={label} className="border-b border-white/5">
                      <td className="p-4 text-slate-500 sticky left-0 bg-[#0B132B]">{label}</td>
                      {saved.slice(0, 4).map((p) => <td key={p.id} className="p-4 text-slate-200 capitalize">{fn(p)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="messages" className="mt-6">
          <ChatInbox me={user} />
        </TabsContent>

        <TabsContent value="enquiries" className="mt-6 space-y-3">
          {enq.length === 0 ? <div className="text-slate-500 py-12 text-center">No enquiries yet.</div> :
            enq.map((e) => (
              <div key={e.id} className="bg-[#0B132B] border border-white/10 rounded-2xl p-5 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-semibold text-white capitalize">{e.type.replace("_", " ")} · {e.property_title}</div>
                  <div className="text-xs text-slate-500">{fmtDate(e.created_at)}</div>
                </div>
                <span className="text-xs px-3 py-1 rounded-full bg-[#C5A059]/10 text-[#C5A059] border border-[#C5A059]/20 capitalize">{e.status.replace(/_/g, " ")}</span>
              </div>
            ))}
        </TabsContent>

        <TabsContent value="checklist" className="mt-6">
          <div className="bg-[#0B132B] border border-white/10 rounded-2xl p-6 max-w-2xl">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h3 className="text-lg font-bold text-white">Before You Pay A Token</h3>
              <span className="text-sm font-semibold text-[#C5A059]">{done} / {CHECKS.length} checks completed</span>
            </div>
            {saved.length > 0 && (
              <Select value={checkProp} onValueChange={setCheckProp}>
                <SelectTrigger className="mt-4 bg-[#131B35] border-white/10 text-white" data-testid="checklist-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent>{saved.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <div className="mt-5 space-y-1">
              {CHECKS.map((c, i) => (
                <label key={c} data-testid={`check-${i}`} className="flex items-center gap-3 py-2.5 border-b border-white/5 cursor-pointer">
                  <Checkbox checked={!!checks[c]} onCheckedChange={() => toggleCheck(c)} disabled={!checkProp} />
                  <span className={`text-sm ${checks[c] ? "text-slate-500 line-through" : "text-slate-200"}`}>{c}</span>
                </label>
              ))}
            </div>
            <p className="mt-4 text-xs text-slate-500">This checklist is informational and does not replace legal, financial or technical professional advice.</p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Empty = ({ nav }) => (
  <div className="text-center py-16">
    <p className="text-slate-500">Your shortlist is empty.</p>
    <Button data-testid="browse-btn" onClick={() => nav("/search")} className="mt-4 bg-[#C5A059] text-[#070C1E] font-semibold">Browse properties</Button>
  </div>
);
